import React from 'react';
import { X } from 'lucide-react';
import { SCORING_GUIDE, SkillGuide } from '../data/scoringGuide';

interface ScoringGuideModalProps {
  skillKey: string;
  isOpen: boolean;
  onClose: () => void;
}

const scoreColor = (s: number) => {
  if (s >= 9) return { bg: 'bg-green-500', border: 'border-green-300', card: 'bg-green-50' };
  if (s >= 7) return { bg: 'bg-lime-500', border: 'border-lime-300', card: 'bg-lime-50' };
  if (s >= 5) return { bg: 'bg-yellow-500', border: 'border-yellow-300', card: 'bg-yellow-50' };
  if (s >= 3) return { bg: 'bg-orange-500', border: 'border-orange-300', card: 'bg-orange-50' };
  return { bg: 'bg-red-500', border: 'border-red-300', card: 'bg-red-50' };
};

const categoryStyle: Record<string, string> = {
  technical: 'bg-red-100 text-red-800',
  physical: 'bg-yellow-100 text-yellow-800',
  psychological: 'bg-green-100 text-green-800',
  social: 'bg-blue-100 text-blue-800',
};

export const ScoringGuideModal: React.FC<ScoringGuideModalProps> = ({ skillKey, isOpen, onClose }) => {
  if (!isOpen) return null;
  const guide: SkillGuide | undefined = SCORING_GUIDE[skillKey];
  if (!guide) return null;

  const sorted = [...guide.levels].sort((a, b) => b.score - a.score);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{guide.skill}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Scoring Guide</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-4">
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${categoryStyle[guide.category] || 'bg-gray-100 text-gray-700'}`}>
            {guide.category}
          </span>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-gray-600 leading-relaxed">{guide.overview}</p>
          </div>

          <div className="space-y-3">
            {sorted.map((level) => {
              const c = scoreColor(level.score);
              return (
                <div key={level.score} className={`rounded-xl border-l-4 ${c.border} ${c.card} p-4`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-8 h-8 ${c.bg} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                      {level.score}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">{level.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 ml-11">{level.description}</p>
                  <ul className="ml-11 space-y-1">
                    {level.examples.map((ex, i) => (
                      <li key={i} className="text-sm text-gray-500 flex items-start gap-2">
                        <span className="text-gray-300 mt-1.5 flex-shrink-0">•</span>
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
